import { styled } from "@mui/material/styles";
import { Box, Skeleton, TextField, Typography } from "@mui/material";
import { LoadingOverlay } from "components/common/LoadingOverlay";

export const Container = styled(Box)(({ theme }) => ({
  position: "relative",
  display: "flex",
  // The panes fill the height left by the page chrome and the list inside them
  // takes what the controls do not, so the button under it is always in view.
  alignItems: "stretch",
  height: "calc(100vh - 235px)",
  minHeight: "460px",
  boxSizing: "border-box",
  margin: "40px 60px",
  padding: "40px 80px",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: "4px",
  backgroundColor: theme.palette.background.paper,
}));

export const PaneWrapper = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  flex: 1,
  minWidth: 0,
}));

export const ActionColumn = styled(Box)(() => ({
  flex: "0 0 220px",
  alignSelf: "stretch",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

export const CategorySetBox = styled(Box)(({ theme }) => ({
  marginTop: "12px",
  flex: 1,
  minHeight: 0,
  overflowY: "auto",
  padding: "8px",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: "4px",
  backgroundColor: theme.palette.background.paper,
  overflowWrap: "anywhere",
  // Drawn as a shadow so the border stays a stable 1px while focused.
  "&:focus, &:focus-within": {
    outline: "none",
    borderColor: theme.palette.focusColor.main,
    boxShadow: `0 0 0 1px ${theme.palette.focusColor.main}`,
  },
}));

export const SearchField = styled(TextField)(({ theme }) => ({
  "& .MuiInputBase-input, & .MuiInputBase-input::placeholder": {
    fontSize: "14px",
    fontFamily: "Helvetica",
    fontWeight: 400,
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: theme.palette.divider,
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: theme.palette.text.primary,
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline, &.Mui-focused .MuiOutlinedInput-notchedOutline":
    {
      borderColor: theme.palette.focusColor.main,
      borderWidth: "1.5px",
    },
  "& .MuiInputLabel-root.Mui-focused, & .MuiFormLabel-root.Mui-focused": {
    color: theme.palette.focusColor.main,
  },
}));

export const CategorySetRow = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "selected" && prop !== "anchor",
})<{ selected?: boolean; anchor?: boolean }>(({ theme, selected, anchor }) => ({
  // A custom Typography variant renders as a span, so the row has to be a
  // block for the selection to span the list.
  display: "block",
  fontWeight: anchor ? 700 : 400,
  textAlign: "left",
  padding: "2px 4px",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  cursor: "pointer",
  userSelect: "none",
  backgroundColor: selected ? theme.palette.action.selected : "transparent",
  "&:hover": { backgroundColor: theme.palette.action.hover },
}));

// Link carries an icon and Unlink does not, so both are pinned to one height.
export const ActionButtonBox = styled(Box)(() => ({
  "& .MuiButton-root": {
    height: "36px",
  },
}));

export const StatusMessage = styled(Typography)(() => ({
  minHeight: "1.2em",
}));

export const LinkingOverlay = styled(LoadingOverlay)(() => ({
  backgroundColor: "transparent",
  backdropFilter: "none",
}));

// Stands in for a select while its options load, keeping the same footprint.
export const DropdownSkeleton = styled(Skeleton)(() => ({
  width: "100%",
  height: "50px",
  marginBottom: "18px",
}));
